<template>
  <c-box v-if="user && user.partnerId" :mt="{ base: '2.6rem', md: '4xl' }">
    <c-flex
      :flex-direction="{ base: 'column-reverse', md: 'row' }"
      :justify="{ base: 'flex-start', md: 'space-between' }"
      :align="{ base: 'flex-start', md: 'center' }"
      wrap="nowrap"
      w="full"
    >
      <c-heading
        as="h1"
        align-self="flex-start"
        :mb="{ base: 'xl', md: '4xl' }"
        :font-size="{ base: '3xl', md: '4xl', xl: '5xl' }"
        >{{ $t("tickets.list.heading") }}</c-heading
      >
      <c-box align-self="flex-start" :mb="{ base: 'xl', md: '0' }">
        <router-link to="/support/requests/create" data-cy="createNewSupportRequest">
          <c-button
            m="0 auto"
            variation="solid"
            background="brand.beige"
            border-color="brand.beige"
            tabindex="-1"
          >
            {{ $t("tickets.newTicket") }}
          </c-button>
        </router-link>
      </c-box>
    </c-flex>
    <c-flex
      :flex-direction="{ base: 'column', md: 'row' }"
      :justify="{ base: 'flex-start', md: 'space-between' }"
      wrap="nowrap"
      w="full"
    >
      <c-flex flex="1" mt="2xs" mb="3xl" :mr="{ base: 0, md: '2xl' }">
        <c-box :d="{ base: 'none', md: 'block' }">
          <c-icon name="search" size="3rem" mr="md" color="brand.purple" />
        </c-box>
        <c-box width="100%" max-width="34rem">
          <c-input
            id="searchTerm"
            position="relative"
            px="0"
            pb="15px"
            :is-full-width="true"
            bg="transparent"
            color="brand.purple"
            :_placeholder="{ color: 'brand.purple' }"
            border-radius="0"
            border-width="0px"
            border-bottom-width="1px"
            border-bottom-color="brand.purple"
            focus-border-color="transparent"
            v-model="term"
            type="text"
            autocomplete="off"
            :placeholder="$t('tickets.ticketSearch.placeholder')"
            @keydown="handleSearchInput(term)"
            @keyup="handleSearchInput(term)"
            @blur="handleSearchInput(term)"
            @keydown.esc="
              term = '';
              handleSearchInput(term);
            "
            :_hover="{ borderBottomColor: 'brand.purple' }"
            :_focus="{ borderBottomColor: 'brand.purple' }"
            :_active="{ borderBottomColor: 'brand.purple' }"
          />
        </c-box>
        <c-box :d="{ base: 'block', md: 'none' }">
          <c-icon name="search" size="3rem" ml="md" color="brand.purple" />
        </c-box>
      </c-flex>
      <c-form-control m="0">
        <c-flex
          :flex-direction="{ base: 'column', sm: 'row' }"
          position="relative"
          :align-items="{ sm: 'center' }"
          v-chakra="{ 'input[type=checkbox]:checked+div': { background: 'brand.purple' } }"
        >
          <c-form-label html-for="hide-closed">
            {{ $t("tickets.filter.hideClosed") }}
          </c-form-label>
          <!-- NOTE: this component's color attribute doesn't seem to work with theme colors or hexadecimals -->
          <c-switch
            id="hide-closed"
            size="lg"
            display="flex"
            mr="auto"
            ml="xs"
            v-model="hideClosedTickets"
            :is-checked="hideClosedTickets"
            :default-is-checked="hideClosedTickets"
            @change="handleStatusFilter()"
            data-cy="hide-closed-tickets-toggle"
          />
        </c-flex>
      </c-form-control>
    </c-flex>

    <c-box v-if="user && user.partnerId">
      <!-- Trying to fetch tickets for the first time -->
      <c-box v-if="supportRequests === null && ticketsLoading">
        <c-heading as="h3">
          {{ $t("tickets.loadingTickets") }}
        </c-heading>
      </c-box>

      <!-- Tried to fetch tickets but none were found -->
      <c-box
        v-else-if="
          !ticketsLoading &&
          supportRequests &&
          supportRequests.values &&
          supportRequests.values.length === 0
        "
      >
        <c-heading as="h3">
          {{ $t("tickets.noTicketsFound") }}
        </c-heading>
      </c-box>

      <c-box v-else-if="supportRequests && supportRequests.values">
        <c-list list-style="none" mt="3xl">
          <c-list-item v-for="supportRequest in supportRequests.values" :key="supportRequest.id">
            <c-box :mb="{ base: 'xs', md: '2xl' }">
              <router-link :to="$route.path + '/' + supportRequest.id">
                <c-box
                  :border-radius="{ md: 'base' }"
                  :background="getBackgroundColorByStatus(supportRequest.status)"
                  :box-shadow="{ md: 'base' }"
                  :px="{ base: 'md', sm: '2xl' }"
                  :py="{ base: 'md', sm: 'xl' }"
                  :mx="{ base: '-31px', lg: '0' }"
                >
                  <c-flex>
                    <c-box
                      w="16px"
                      h="16px"
                      :bg="getBallColorByStatus(supportRequest.status)"
                      border-radius="8px"
                      mt="6px"
                      min-width="16px"
                    />

                    <c-flex ml="lg" flex-direction="column" my="xs">
                      <c-heading
                        as="h3"
                        font-size="lg"
                        font-weight="bold"
                        color="brand.purple"
                        mb="xs"
                      >
                        {{ getTitlePrefixByStatus(supportRequest.status)
                        }}{{ supportRequest.subject }}
                      </c-heading>

                      <c-text
                        as="span"
                        font-size="xs"
                        color="#4b4b4b"
                        :mb="{ base: '0', md: 'lg' }"
                      >
                        <c-text
                          as="span"
                          :d="{ base: 'block', md: 'inline-block' }"
                          :mr="{ base: 0, md: 'lg' }"
                          :mb="{ base: '2xs', md: 0 }"
                        >
                          <c-text as="span"> {{ $t("tickets.ticketId") }}: </c-text>
                          <c-text as="span" font-weight="bold">{{ supportRequest.id }}</c-text>
                        </c-text>

                        <c-text
                          as="span"
                          :d="{ base: 'block', md: 'inline-block' }"
                          :mr="{ base: 0, md: 'lg' }"
                          :mb="{ base: '2xs', md: 0 }"
                        >
                          <c-text as="span"> {{ $t("tickets.latestMessage") }}: </c-text>
                          <c-text
                            v-if="getLatestConversation(supportRequest)"
                            as="span"
                            font-weight="bold"
                            mr="lg"
                            >{{
                              getLatestConversation(supportRequest).createdAt
                                | moment("DD.MM.YYYY HH:mm")
                            }},
                            <c-text as="span" data-cy="openedBy">{{
                              getLatestConversation(supportRequest)?.name
                            }}</c-text>
                          </c-text>
                          <c-text v-else as="span">-</c-text>
                        </c-text>

                        <c-text as="span" :d="{ base: 'block', md: 'inline-block' }">
                          <c-text as="span"> {{ $t("tickets.opened") }}: </c-text>
                          <c-text as="span" font-weight="bold"
                            >{{ supportRequest.createdAt | moment("DD.MM.YYYY HH:mm") }},
                            <c-text as="span" data-cy="openedBy">{{ supportRequest.name }}</c-text>
                          </c-text>
                        </c-text>
                      </c-text>

                      <c-box
                        :d="{ base: 'none', md: 'block' }"
                        font-size="sm"
                        color="text"
                        max-height="150px"
                        overflow="hidden"
                        :style="'mask-image: linear-gradient(to bottom, black 100px, transparent 100%);'"
                        v-chakra="{
                          '.pp-field-label': {
                            'font-weight': 'bold',
                          },
                        }"
                      >
                        <span v-html="supportRequest.description" />
                      </c-box>
                    </c-flex>
                  </c-flex>
                </c-box>
              </router-link>
            </c-box>
          </c-list-item>
        </c-list>

        <InfiniteScroller
          v-if="supportRequests.values && supportRequests.values.length"
          :results="supportRequests.values"
          :result-count="supportRequests.count"
          :action="() => getMoreTickets()"
          :loading-more="ticketsLoading"
        />
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import InfiniteScroller from "@/components/InfiniteScroller.vue";
import SearchInput from "@/components/SearchInput.vue";
import DownloadSupportRequestAttachment from "@/components/solutions/DownloadSupportRequestAttachment.vue";
import config from "@/config";
import Button from "@/design-system/elements/Button.vue";
import ButtonGroup from "@/design-system/elements/ButtonGroup.vue";
import Card from "@/design-system/elements/Card.vue";
import Heading from "@/design-system/elements/Heading.vue";
import { IRootState } from "@iloq/shared";
import { ISolutionsState } from "@/store/solutionsModule";
import { debounce, isEqual } from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";

import QuickSolutionSearch from "@/components/solutions/QuickSolutionSearch.vue";
import {
  CBox,
  CButton,
  CFlex,
  CFormControl,
  CFormLabel,
  CHeading,
  CIcon,
  CImage,
  CInput,
  CList,
  CListItem,
  CSwitch,
  CText,
} from "@chakra-ui/vue";
import { ISupportRequest, TicketStatus } from "@iloq/shared";

type StatusFilter = "any" | "open" | "closed";

type SupportRequestQueryParams = {
  term?: string;
  status: StatusFilter;
  top: string;
  skip: string;
};

export default defineComponent({
  name: "SupportRequestList",
  components: {
    CIcon,
    CInput,
    CButton,
    CBox,
    QuickSolutionSearch,
    CFlex,
    CFormLabel,
    CFormControl,
    CHeading,
    CImage,
    CList,
    CListItem,
    CSwitch,
    CText,
    Button,
    ButtonGroup,
    SearchInput,
    Heading,
    Card,
    DownloadSupportRequestAttachment,
    InfiniteScroller,
  },
  computed: {
    ...mapState("solutions", {
      supportRequests: (state: unknown) => (state as ISolutionsState).supportRequests,
      ticketsLoading: (state: unknown) => (state as ISolutionsState).ticketsLoading,
    }),
    ...mapState({
      user: (state: unknown) => (state as IRootState).user,
    }),
  },
  data() {
    return {
      term: "",
      hideClosedTickets: false,
      debouncedfetchSupportRequests: null as any,
    };
  },
  beforeMount() {
    const { query } = this.$route;
    this.term = (query.term as string) ?? "";
    this.hideClosedTickets = (query.hideClosed as string) === "true";
    const defaultQuery = {
      term: this.term,
      status: this.hideClosedTickets ? "open" : "any",
      top: `${this.getPageSize()}`,
      skip: "0",
    };

    if (!isEqual(defaultQuery, this.$route.query)) {
      this.$router.push({
        query: defaultQuery,
      });
    } else {
      // Needed when opening the page with default query in place
      // (in that case the query is not changing on this.$router.push(...) above so supportRequestWatcher() gets not called)
      this.fetchSupportRequests(this.$route.query as SupportRequestQueryParams);
    }

    this.debouncedfetchSupportRequests = debounce(this.fetchSupportRequests, 250);
  },
  methods: {
    getTitlePrefixByStatus(status: TicketStatus) {
      if ([TicketStatus.Closed].indexOf(status) >= 0) {
        return `${this.$t(`tickets.titlePrefix.${TicketStatus[status]}`)}: `;
      }
      return "";
    },
    getBallColorByStatus(status: TicketStatus) {
      if ([TicketStatus.Closed].indexOf(status) >= 0) {
        return "brand.grayDark";
      }
      return "brand.green";
    },
    getBackgroundColorByStatus(status: TicketStatus) {
      if ([TicketStatus.Closed].indexOf(status) >= 0) {
        return "brand.gray";
      }
      return "white";
    },
    getMoreTickets() {
      if (!this.ticketsLoading) {
        this.updatePageState({
          skip: Number(this.$route.query.skip) + this.getPageSize(),
          top: this.$route.query.top as string,
        });
      }
    },
    updatePageState({ top, skip }: { top: string; skip: string }) {
      const { query } = this.$route;
      this.$router.replace({
        query: {
          ...query,
          top,
          skip,
        },
      });
    },
    getPageSize() {
      // Set this to a small number like 4 in the config to test paging locally
      return config.get("supportTicketSearchPageSize");
    },
    handleStatusFilter() {
      this.$router.push({
        query: {
          ...this.$route.query,
          status: this.hideClosedTickets ? "open" : "any",
          top: this.getPageSize(),
          skip: "0",
        },
      });
    },
    handleSearchInput(value: string) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          term: value,
          top: this.getPageSize(),
          skip: "0",
        },
      });
    },
    fetchSupportRequests(query: SupportRequestQueryParams) {
      const { top, skip, ...rest } = query;
      this.$store.dispatch("solutions/fetchSupportRequests", {
        ...rest,
        pageState: {
          top: Number(top),
          skip: Number(skip),
        },
      });
    },
    getLatestConversation(req: ISupportRequest): { createdAt: Date; name: string } | undefined {
      if (req.conversation.length > 0) {
        return {
          createdAt: req.conversation[req.conversation.length - 1].createdAt,
          name: req.conversation[req.conversation.length - 1].name,
        };
      }
    },
  },
  watch: {
    "user.partnerId": {
      handler() {
        // In some cases the partnerId may get populated "late"
        this.fetchSupportRequests(this.$route.query as SupportRequestQueryParams);
      },
    },
    "$route.query": {
      handler(query: SupportRequestQueryParams) {
        this.debouncedfetchSupportRequests(query);
      },
    },
  },
});
</script>
